// Long-form content for the HVAC consultant page (/hvac-consultant).
//
// Written for the searches people make when they need HVAC advice: HVAC
// consultant(s), HVAC consultation and its cost, HVAC design consultants,
// HVAC engineering consultants, residential HVAC consultant, air conditioning
// consultant, heating and cooling consultants near me.
//
// Read by the page and by the pre-render, so Google gets the same text. No
// imports. Block format as in src/data/designerLibrary.js.

export const CONSULTANT_SECTIONS = [
  {
    id: 'what',
    title: 'What an HVAC consultant does',
    blocks: [
      { t: 'p', text: 'An HVAC consultant, or air conditioning consultant, works out what a building needs to stay cool in summer, dry in the monsoon and warm in winter, and turns that into a design that can be priced and built. Most dealers do this quickly and for free, as part of selling an AC. We do it properly, for a fixed fee, and take the whole fee off your bill if you buy the system from us.' },
      {
        t: 'ul',
        items: [
          'Heat load, room by room, for summer, monsoon and winter.',
          'The right system: split, VRV/VRF, ductable, cassette or chilled water.',
          'Equipment selection: tonnage, star rating and model, for the hours you run it.',
          'Layout drawing: indoor and outdoor units, refrigerant pipes, drains, ducts and grilles.',
          'BOQ: every item and quantity, so quotes can be compared like for like.',
          'Running cost: electricity over five years, so the cheapest AC to buy is not chosen by mistake.',
        ],
      },
    ],
  },
  {
    id: 'cost-detail',
    title: 'What each consultation includes',
    blocks: [
      {
        t: 'table',
        head: ['Service', 'Price', 'What you get', 'Best for'],
        rows: [
          ['Video call · 30 min', '₹99 (was ₹1,000)', 'An HVAC engineer on a video call', 'Choosing an AC, checking a quote, split or VRV'],
          ['Site visit + 1-hr consultation', '₹1,500 (was ₹5,000)', 'An engineer at your site for an hour: what to install, where, and roughly what it costs', 'Renovations, new homes and offices before design'],
          ['Heat load calculation', '₹0.50 a sq ft (was ₹2), minimum ₹750', 'A room-by-room heat load report as a PDF', 'Architects, builders, anyone tendering'],
          ['End-to-end HVAC design', '₹3 a sq ft for homes, ₹4 for offices and shops', 'Site visit, heat load, layout drawing and BOQ', 'New builds and full renovations'],
        ],
        note: 'Prices include GST. Hotels, and sites over 4,000 sq ft, are quoted separately. The whole fee comes off your bill if you buy the system from us.',
      },
      { t: 'p', text: 'Why pay at all? A free estimate is usually a square-feet-per-ton rule done in minutes. A consultation buys an engineer\'s time: real numbers for each room, a drawing and a BOQ you keep. And if you buy the system from us, it costs you nothing in the end.' },
    ],
  },
  {
    id: 'process',
    title: 'How HVAC design works with us',
    blocks: [
      {
        t: 'steps',
        items: [
          'Book online and pay. Our engineer calls within 24 hours to fix a time (Sundays and public holidays excluded).',
          'We visit the site, or go through your drawings on a video call.',
          'We work out the heat load room by room, for summer, monsoon and winter.',
          'We choose the system and the equipment, with the running cost of each option.',
          'You get the layout drawing and the BOQ.',
          'If you go ahead with us, we install, test and commission the system, and service it afterwards. The consultation fee comes off your bill.',
        ],
      },
    ],
  },
  {
    id: 'residential',
    title: 'Residential HVAC consultant: homes, villas and flats',
    blocks: [
      { t: 'p', text: 'A home is not an office. Bedrooms run at night, living rooms in the evening, and the top floor bakes under the roof. We design for the way the family lives.' },
      {
        t: 'ul',
        items: [
          'Top floors: the roof adds a large share of the heat. We check whether insulating it saves more than a bigger AC would cost.',
          'West-facing glass: the afternoon sun can need more cooling than everything else in the room.',
          'Bedrooms: quiet units, and no air blowing on the bed.',
          'Hidden or visible: ducted, cassette or wall-mounted units to suit the interiors.',
          'Villas and large homes: Daikin VRV, with one outdoor system on the terrace for every room.',
          'Pipes before plaster: routes planned so nothing shows on the finished walls.',
          'Outdoor units: placed where they get air and stay out of sight.',
        ],
      },
    ],
  },
  {
    id: 'commercial',
    title: 'Commercial HVAC engineering consultants',
    blocks: [
      { t: 'p', text: 'As HVAC design and engineering consultants for offices, shops, restaurants and factories, we design for the load, the hours the space runs, and what it will cost to run.' },
      {
        t: 'table',
        head: ['Space', 'What we design for'],
        rows: [
          ['Offices and co-working', 'Cabins, meeting rooms, open floors and server rooms, each with its own control.'],
          ['Retail and showrooms', 'Lighting heat, glass shopfronts and busy doors.'],
          ['Restaurants and cafes', 'Kitchen heat and exhaust, with fresh air to replace it.'],
          ['Clinics and labs', 'Fresh air and filtration.'],
          ['Factories and warehouses', 'Spot cooling or ductable systems for large spaces.'],
          ['Hotels and large sites', 'VRV to chilled water, quoted separately.'],
        ],
      },
    ],
  },
  {
    id: 'heating',
    title: 'Heating and cooling, one system',
    blocks: [
      { t: 'p', text: 'Delhi NCR needs heating for a few weeks every winter. Daikin VRV and hot and cold splits heat as well as cool, so one system covers the whole year. Our heat load works out the winter heating as well as the summer cooling, and the humid monsoon load, which can be the one that sets the size.' },
    ],
  },
  {
    id: 'report',
    title: 'What a proper heat load looks like',
    blocks: [
      { t: 'p', text: 'This is our report format, room by room and season by season, following the ASHRAE and ISHRAE methods used in Daikin\'s heat load sheet. The values in the sample are hidden; your report has every number for your project.' },
      { t: 'sample' },
    ],
  },
  {
    id: 'why',
    title: 'Why a consultant who also installs',
    blocks: [
      {
        t: 'ul',
        items: [
          'One team answers for the design and for the result.',
          'Sales and service under one roof after handover: no running around.',
          'Cooling Delhi since 1990. Authorized Daikin dealer since 2003, and Daikin\'s No. 1 dealer in the North region.',
          '1,500+ projects across homes, offices, showrooms and factories.',
          'Honest sizing: our AC calculator and our heat loads use the same Daikin method.',
        ],
      },
    ],
  },
  {
    id: 'near',
    title: 'HVAC consultants near you',
    blocks: [
      { t: 'p', text: 'Heating and cooling consultants across Delhi NCR: site visits in Delhi, Gurugram, Noida, Greater Noida, Faridabad and Ghaziabad. Showroom: 13 Jain Mandir Market, South Extension Part 2, New Delhi 110049, Monday to Saturday, 9 am to 6 pm. The video call and the heat load calculation are done online, wherever your project is.' },
    ],
  },
];

export const CONSULTANT_TOOLKIT = [
  { label: 'AC size calculator', href: '/ac-calculator', text: 'Tonnage for any room in 54 Indian cities, with the running cost.' },
  { label: 'Ceiling depth checker', href: '/architects-interior-designers#ceiling-tool', text: 'How deep the false ceiling must be for a ducted AC or cassette.' },
  { label: 'HVAC coordination checklist', href: '/architects-interior-designers#checklist', text: 'What to plan at each stage of a project. Printable.' },
  { label: 'Indoor unit corner', href: '/architects-interior-designers#units', text: 'Every Daikin VRV indoor unit: the ceiling it needs, its tons and its limits.' },
  { label: 'Daikin AC price list', href: '/daikin-ac-price-list-delhi', text: 'Live prices and stock, GST included.' },
  { label: 'BEE star labels', href: 'https://beestarlabel.com', text: 'Check any AC\'s star rating and yearly units.', external: true },
];

export const CONSULTANT_FAQ = [
  { q: 'How much does an HVAC consultation cost?', a: 'A 30-minute video call is ₹99. A site visit with a one-hour consultation is ₹1,500. A heat load calculation is ₹0.50 a sq ft, minimum ₹750. End-to-end HVAC design, with site visit, heat load, drawing and BOQ, is ₹3 a sq ft for homes and ₹4 for offices and shops. All prices include GST.' },
  { q: 'What does an HVAC design consultant do?', a: 'Works out the heat load of every room, chooses the system and equipment, and draws the layout with a bill of quantities, so the system can be priced and built correctly.' },
  { q: 'Is the fee adjusted if I buy from you?', a: 'Yes. If you buy the system from us, the whole consultation fee comes off your bill.' },
  { q: 'Do the prices include GST?', a: 'Yes. Every price on this page includes GST.' },
  { q: 'What is included in end-to-end HVAC design?', a: 'A site visit, the room-by-room heat load, the system and equipment selection, a layout drawing of indoor and outdoor units, pipes, drains and ducts, and a BOQ.' },
  { q: 'How is your heat load different from a free estimate?', a: 'A free estimate is usually a square-feet-per-ton rule. Ours goes room by room, for summer, monsoon and winter, using walls, roof, glass, people, lighting, equipment and fresh air, the way Daikin\'s heat load sheet does.' },
  { q: 'Is a residential HVAC consultation different from a commercial one?', a: 'The method is the same. Homes are ₹3 a sq ft and offices and shops ₹4 for end-to-end design, because commercial spaces have more zones, fresh air and equipment to work through.' },
  { q: 'When will you call?', a: 'Our engineer calls you within 24 hours to fix a time. Sundays and public holidays excluded.' },
  { q: 'Where do you do site visits?', a: 'Across Delhi NCR. The video call and the heat load calculation are done online, wherever the project is.' },
  { q: 'My site is a hotel, or bigger than 4,000 sq ft.', a: 'We quote for it separately. Call or WhatsApp us.' },
  { q: 'Do you design VRV systems?', a: 'Yes. We are an authorized Daikin dealer and design, install and service Daikin VRV for homes, offices and showrooms.' },
  { q: 'Do you work with architects and interior designers?', a: 'Yes. When we do the job, the HVAC design for your client is free, and you get a referral fee or trade pricing. See the page for architects and interior designers.' },
  { q: 'Do you handle heating too?', a: 'Yes. Daikin VRV and hot and cold splits heat in winter, and our heat load covers winter as well as summer.' },
];
